// Admin-only "Access Control" page. Two tabs:
//   Accounts — manage an individual account's role + individual permission grants.
//   Roles    — manage role permissions and the role lifecycle (create/rename/delete).
// Gated by the caller (App renders this only when the operator is an admin); the
// backend RPCs independently re-verify admin, so this is defence-in-depth, not the
// sole gate.
import { useCallback, useEffect, useState } from 'react';
import { Operator, Role, PermissionDef, AdminOperator } from '../types/database';
import { adminAccessService } from '../services/adminAccess';
import { clearPermissionsCache, preloadAllRolePermissions } from '../services/rolePermissions';
import AccountsPanel from '../components/admin/AccountsPanel';
import RolesPanel from '../components/admin/RolesPanel';
import '../styles/admin-access.css';

interface AdminAccessViewProps {
  actor: Operator;
}

type Tab = 'accounts' | 'roles';

export default function AdminAccessView({ actor }: AdminAccessViewProps) {
  const [tab, setTab] = useState<Tab>('accounts');
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionDef[]>([]);
  const [rolePermsByRole, setRolePermsByRole] = useState<Map<string, Set<string>>>(new Map());
  const [operators, setOperators] = useState<AdminOperator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [rolesData, permsData, rolePerms, ops] = await Promise.all([
        adminAccessService.listRoles(),
        adminAccessService.listPermissions(),
        adminAccessService.listRolePermissions(),
        adminAccessService.listOperators(actor),
      ]);
      setRoles(rolesData);
      setPermissions(permsData);
      const map = new Map<string, Set<string>>();
      for (const { role, permission } of rolePerms) {
        if (!map.has(role)) map.set(role, new Set());
        map.get(role)!.add(permission);
      }
      setRolePermsByRole(map);
      setOperators(ops);

      // Keep the running session's permission cache in step with edits made here.
      clearPermissionsCache();
      await preloadAllRolePermissions();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="access-view">
      <div className="access-tabs">
        <button className={`access-tab ${tab === 'accounts' ? 'active' : ''}`} onClick={() => setTab('accounts')}>
          Accounts
        </button>
        <button className={`access-tab ${tab === 'roles' ? 'active' : ''}`} onClick={() => setTab('roles')}>
          Roles
        </button>
      </div>

      {error && <div className="access-error">{error}</div>}

      {loading ? (
        <div className="access-detail-empty">Loading…</div>
      ) : tab === 'accounts' ? (
        <AccountsPanel
          actor={actor}
          operators={operators}
          roles={roles}
          permissions={permissions}
          rolePermsByRole={rolePermsByRole}
          onChanged={load}
          onError={setError}
        />
      ) : (
        <RolesPanel
          actor={actor}
          roles={roles}
          permissions={permissions}
          rolePermsByRole={rolePermsByRole}
          operators={operators}
          onChanged={load}
          onError={setError}
        />
      )}
    </div>
  );
}
